// src/components/payroll/timesheets/TimesheetList.js
import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { AppContext } from '../../../context/AppContext';
import { db } from '../../../firebase/config';
import { 
  collection, query, where, orderBy, limit, 
  startAfter, getDocs, doc, getDoc, updateDoc 
} from 'firebase/firestore';
import {
  Search, Clock, AlertCircle, Check, X, Filter,
  ChevronLeft, Calendar, CheckCircle, AlertTriangle
} from 'lucide-react';

const TimesheetList = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { activeBusiness } = useContext(AppContext);
  
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  
  // Fetch time entries
  useEffect(() => {
    const fetchTimeEntries = async () => {
      if (!currentUser || !activeBusiness) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Build query
        let timeEntriesQuery = query(
          collection(db, 'timeEntries'),
          where('businessId', '==', activeBusiness.id)
        );
        
        // Apply status filter if not 'all'
        if (statusFilter !== 'all') {
          timeEntriesQuery = query(
            timeEntriesQuery,
            where('status', '==', statusFilter)
          );
        }
        
        // Order by date
        timeEntriesQuery = query(
          timeEntriesQuery,
          orderBy('date', 'desc'),
          limit(15)
        );
        
        const entriesSnap = await getDocs(timeEntriesQuery);
        const lastVisible = entriesSnap.docs[entriesSnap.docs.length - 1];
        
        // Process entries with employee details
        const entriesWithEmployeesPromises = entriesSnap.docs.map(async (doc) => {
          const data = doc.data();
          
          // Get employee details
          let employee = { id: data.employeeId };
          try {
            const empDoc = await getDoc(doc(db, 'teamMembers', data.employeeId));
            if (empDoc.exists()) {
              const empData = empDoc.data();
              employee = {
                ...employee,
                firstName: empData.firstName || '',
                lastName: empData.lastName || '',
                email: empData.email || '',
                position: empData.position || 'Employee'
              };
            }
          } catch (err) {
            console.error(`Error fetching employee ${data.employeeId}:`, err);
          }
          
          return {
            id: doc.id,
            ...data,
            date: data.date?.toDate() || null,
            employee
          };
        });
        
        const entriesData = await Promise.all(entriesWithEmployeesPromises);
        
        setTimeEntries(entriesData);
        setLastVisible(lastVisible);
        setHasMore(entriesData.length === 15);
        
      } catch (err) {
        console.error('Error fetching time entries:', err);
        setError('Failed to load time entries. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTimeEntries();
  }, [currentUser, activeBusiness, statusFilter]);
  
  // Load more time entries
  const handleLoadMore = async () => {
    if (!hasMore || loading || !lastVisible) return;
    
    try {
      setLoading(true);
      
      // Build query with the same filters
      let nextQuery = query(
        collection(db, 'timeEntries'),
        where('businessId', '==', activeBusiness.id)
      );
      
      // Apply status filter if not 'all'
      if (statusFilter !== 'all') {
        nextQuery = query(
          nextQuery,
          where('status', '==', statusFilter)
        );
      }
      
      // Continue from last visible doc
      nextQuery = query(
        nextQuery,
        orderBy('date', 'desc'),
        startAfter(lastVisible),
        limit(15)
      );
      
      const entriesSnap = await getDocs(nextQuery);
      const newLastVisible = entriesSnap.docs[entriesSnap.docs.length - 1];
      
      // Process entries with employee details
      const newEntriesPromises = entriesSnap.docs.map(async (doc) => {
        const data = doc.data();
        
        // Get employee details
        let employee = { id: data.employeeId };
        try {
          const empDoc = await getDoc(doc(db, 'teamMembers', data.employeeId));
          if (empDoc.exists()) {
            const empData = empDoc.data();
            employee = {
              ...employee,
              firstName: empData.firstName || '',
              lastName: empData.lastName || '',
              email: empData.email || '',
              position: empData.position || 'Employee'
            };
          }
        } catch (err) {
          console.error(`Error fetching employee ${data.employeeId}:`, err);
        }
        
        return {
          id: doc.id,
          ...data,
          date: data.date?.toDate() || null,
          employee
        };
      });
      
      const newEntriesData = await Promise.all(newEntriesPromises);
      
      setTimeEntries(prev => [...prev, ...newEntriesData]);
      setLastVisible(newLastVisible);
      setHasMore(newEntriesData.length === 15);
      
    } catch (err) {
      console.error('Error loading more time entries:', err);
      setError('Failed to load more time entries. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Approve a time entry
  const handleApprove = async (entryId) => {
    try {
      await updateDoc(doc(db, 'timeEntries', entryId), {
        status: 'approved',
        approvedAt: new Date(),
        approvedBy: currentUser.uid
      });
      
      // Update local state
      setTimeEntries(prev => 
        prev.map(entry => 
          entry.id === entryId 
            ? { ...entry, status: 'approved', approvedAt: new Date(), approvedBy: currentUser.uid }
            : entry
        )
      );
      
    } catch (err) {
      console.error('Error approving time entry:', err);
      alert('Failed to approve time entry. Please try again.');
    }
  };
  
  // Reject a time entry
  const handleReject = async (entryId) => {
    try {
      await updateDoc(doc(db, 'timeEntries', entryId), {
        status: 'rejected',
        rejectedAt: new Date(),
        rejectedBy: currentUser.uid
      });
      
      // Update local state
      setTimeEntries(prev => 
        prev.map(entry => 
          entry.id === entryId 
            ? { ...entry, status: 'rejected', rejectedAt: new Date(), rejectedBy: currentUser.uid }
            : entry
        )
      );
      
    } catch (err) {
      console.error('Error rejecting time entry:', err);
      alert('Failed to reject time entry. Please try again.');
    }
  };
  
  // Filter time entries by search term
  const getFilteredTimeEntries = () => {
    if (!searchTerm.trim()) return timeEntries;
    
    const searchLower = searchTerm.toLowerCase();
    return timeEntries.filter(
      entry => 
        (entry.employee?.firstName?.toLowerCase().includes(searchLower)) ||
        (entry.employee?.lastName?.toLowerCase().includes(searchLower)) ||
        (entry.description?.toLowerCase().includes(searchLower))
    );
  };
  
  // Format date for display
  const formatDate = (date) => {
    if (!date) return 'Not set';
    try {
      return new Date(date).toLocaleDateString();
    } catch (e) {
      return 'Invalid date';
    }
  };
  
  const filteredTimeEntries = getFilteredTimeEntries();
  
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <button
          onClick={() => navigate('/payroll')}
          className="text-[#190192] hover:text-[#2C0DB5] flex items-center"
        >
          <ChevronLeft size={16} className="mr-1" />
          Back to Payroll
        </button>
        <h1 className="mt-2 text-xl font-bold text-gray-900">Timesheet Entries</h1>
      </div>
      
      <div className="p-6">
        {/* Search and filters */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              className="pl-10 pr-4 py-2.5 w-full rounded-lg text-sm border border-gray-200 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
              placeholder="Search by employee name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <select
              className="pl-10 pr-4 py-2.5 rounded-lg text-sm border border-gray-200 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20 appearance-none"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Entries</option>
              <option value="pending">Pending Approval</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
        
        {/* Content */}
        {/* Content */}
        {loading && timeEntries.length === 0 ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <div className="animate-spin h-8 w-8 border-4 border-[#190192] rounded-full border-t-transparent"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 rounded-lg p-4 text-center">
            <AlertCircle size={24} className="mx-auto text-red-500 mb-2" />
            <p className="text-red-800">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Try Again
            </button>
          </div>
        ) : filteredTimeEntries.length === 0 ? (
          <div className="text-center py-12">
            <Clock size={48} className="mx-auto text-gray-300 mb-4" />
            {timeEntries.length === 0 ? (
              <>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No timesheet entries found</h3>
                <p className="text-gray-500 mb-6">
                  {statusFilter === 'pending' 
                    ? "There are no pending timesheet entries to approve."
                    : "No timesheet entries have been submitted yet."}
                </p>
                <button
                  onClick={() => setStatusFilter('all')}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  View All Entries
                </button>
              </>
            ) : (
              <>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No matching entries</h3>
                <p className="text-gray-500 mb-4">
                  No timesheet entries match your search criteria.
                </p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Clear Search
                </button>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employee
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hours
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTimeEntries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center text-purple-700">
                            {entry.employee.firstName?.charAt(0) || ''}
                            {entry.employee.lastName?.charAt(0) || ''}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {entry.employee.firstName} {entry.employee.lastName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {entry.employee.position || 'Employee'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <Calendar size={14} className="text-gray-400 mr-2" />
                          {formatDate(entry.date)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {entry.hoursWorked}
                        {entry.overtimeHours > 0 && (
                          <span className="ml-2 text-amber-600">+{entry.overtimeHours} OT</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                        {entry.description || 'No description provided'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          entry.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : entry.status === 'approved'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                        }`}>
                          {entry.status === 'pending' && <Clock size={12} className="mr-1" />}
                          {entry.status === 'approved' && <CheckCircle size={12} className="mr-1" />}
                          {entry.status === 'rejected' && <AlertTriangle size={12} className="mr-1" />}
                          {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {entry.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleApprove(entry.id)}
                                className="p-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200"
                                title="Approve"
                              >
                                <Check size={16} />
                              </button>
                              <button
                                onClick={() => handleReject(entry.id)}
                                className="p-1.5 bg-red-100 text-red-700 rounded hover:bg-red-200"
                                title="Reject"
                              >
                                <X size={16} />
                              </button>
                            </>
                          )}
                          <Link
                            to={`/payroll/timesheets/${entry.id}`}
                            className="text-[#190192] hover:text-[#2C0DB5]"
                          >
                            Details
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Load more button */}
            {hasMore && (
              <div className="mt-6 text-center">
                <button
                  onClick={handleLoadMore}
                  disabled={loading}
                  className={`px-4 py-2 border border-gray-300 rounded-lg ${
                    loading ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {loading ? (
                    <>
                      <div className="inline-block animate-spin h-4 w-4 border-2 border-gray-500 rounded-full border-t-transparent mr-2"></div>
                      Loading...
                    </>
                  ) : (
                    'Load More'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TimesheetList;